import React from 'react';
import { createPortal } from 'react-dom';
import { X, Flag, AlertCircle, CheckCircle, Clock, RefreshCw, XCircle, Calendar, User, AlignLeft } from 'lucide-react';

export default function TaskDetailsModal({ isOpen, onClose, task }) {
  if (!isOpen || !task) return null;

  const getPriorityStyle = (priority) => {
    switch(priority) {
      case 'urgent': return "bg-red-900/40 text-red-400 border border-red-800/50";
      case 'high': return "bg-orange-900/40 text-orange-400 border border-orange-800/50";
      case 'medium': return "bg-blue-900/40 text-blue-400 border border-blue-800/50";
      case 'low': return "bg-slate-800/60 text-slate-400 border border-slate-700/50";
      default: return "bg-slate-800/60 text-slate-400 border border-slate-700/50";
    }
  };

  const getStatusStyle = (status) => {
    switch(status) {
      case 'overdue': return "bg-red-900/40 text-red-400 border border-red-800/50";
      case 'completed': return "bg-emerald-900/40 text-emerald-400 border border-emerald-800/50";
      case 'in_progress': return "bg-blue-900/40 text-blue-400 border border-blue-800/50";
      case 'not_done': return "bg-slate-800/60 text-red-400 border border-red-800/50";
      default: return "bg-amber-900/40 text-amber-400 border border-amber-800/50";
    }
  };

  const formatStatus = (status) => {
    switch(status) {
      case 'overdue': return 'Overdue';
      case 'completed': return 'Completed';
      case 'in_progress': return 'In Progress';
      case 'not_done': return 'Not Done';
      default: return 'Pending';
    }
  };

  const priorityIcon = (priority) => {
    if (priority === 'urgent') return <div className="w-2 h-2 rounded-full bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.8)] animate-pulse"></div>;
    return null;
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-md animate-in fade-in duration-200" onClick={onClose}>
      
      <div 
        className="w-full max-w-2xl bg-[#111322] rounded-3xl shadow-2xl overflow-hidden relative animate-in zoom-in-95 duration-200"
        onClick={e => e.stopPropagation()}
      >
        {/* Dark Top Section */}
        <div className="px-8 pt-8 pb-12 relative overflow-hidden">
          {/* Background Glow */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-purple-600/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
          
          <button 
            onClick={onClose}
            className="absolute top-6 right-6 w-8 h-8 flex items-center justify-center rounded-full bg-white/5 text-slate-400 hover:bg-white/10 hover:text-white transition-colors"
          >
            <X size={16} strokeWidth={2.5} />
          </button>

          {task.category && (
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-slate-300 text-[10px] font-black uppercase tracking-wider mb-4 shadow-sm backdrop-blur-sm">
              <Flag size={12} /> {task.category}
            </div>
          )}

          <h1 className="text-3xl font-black text-white mb-6 tracking-tight drop-shadow-sm">{task.title}</h1>
          
          <div className="flex flex-wrap items-center gap-3">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold ${getStatusStyle(task.status)}`}>
              {task.status === 'overdue' ? <AlertCircle size={14} /> : 
               task.status === 'completed' ? <CheckCircle size={14} /> : 
               <Clock size={14} />} 
              {formatStatus(task.status)}
            </span>
            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold capitalize ${getPriorityStyle(task.priority)}`}>
              {priorityIcon(task.priority)} {task.priority} Priority
            </span>
          </div>
        </div>

        {/* White Inner Card */}
        <div className="bg-slate-50 relative rounded-t-3xl border-t border-white shadow-[0_-10px_40px_rgba(0,0,0,0.1)] -mt-6">
          <div className="p-8 space-y-6">
            
            {/* 4 Info Boxes Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-red-50/50 border border-red-100 rounded-xl p-3 flex flex-col justify-center">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5 mb-1 opacity-80">
                  <Calendar size={12} /> Due Date
                </span>
                <div className="text-[13px] font-black text-red-500">
                  {new Date(task.due_date).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })}
                </div>
              </div>

              <div className="bg-white border border-slate-100 shadow-sm rounded-xl p-3 flex flex-col justify-center">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5 mb-1 opacity-80">
                  <Clock size={12} /> Due Time
                </span>
                <div className="text-[13px] font-black text-slate-700">All Day</div>
              </div>

              <div className="bg-white border border-slate-100 shadow-sm rounded-xl p-3 flex flex-col justify-center">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5 mb-1 opacity-80">
                  <Clock size={12} /> Est. Hours
                </span>
                <div className="text-[13px] font-black text-slate-700">{task.estimated_hours || '-'}</div>
              </div>

              <div className="bg-white border border-slate-100 shadow-sm rounded-xl p-3 flex flex-col justify-center relative overflow-hidden group">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 opacity-80">
                  Assignee
                </span>
                <div className="flex items-center gap-2">
                  <div className="text-[13px] font-black text-slate-700 truncate">{task.assignee?.name?.split(' ')[0] || 'Unknown'}</div>
                  <div className="absolute right-3 w-7 h-7 rounded-full bg-purple-600 text-white flex items-center justify-center font-black text-[11px] shadow-sm transform group-hover:scale-110 transition-transform">
                    {task.assignee?.name?.charAt(0) || 'U'}
                  </div>
                </div>
              </div>
            </div>

            {/* Description Box */}
            <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-6 overflow-hidden">
              <h3 className="text-[11px] font-black uppercase text-slate-800 tracking-widest flex items-center gap-2 mb-4">
                <div className="p-1.5 rounded border border-purple-100 bg-purple-50 text-purple-600">
                  <AlignLeft size={14} />
                </div>
                Description
              </h3>
              <div className="max-h-[200px] overflow-y-auto custom-scrollbar pr-2">
                <p className="text-[13px] font-semibold text-slate-600 leading-relaxed whitespace-pre-wrap">
                  {task.description || 'No description provided for this task.'}
                </p>
              </div>
            </div>

            {/* Footer Assigner Info */}
            <div className="pt-2 flex items-center gap-2 text-slate-400">
              <User size={14} />
              <span className="text-[11px] font-bold">
                Assigned by <span className="text-slate-600">{task.assigner?.name || 'System Admin'}</span>
              </span>
            </div>

          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
